// File: accountingManagement.tsx (updated version)
import React, { useState, useEffect, useMemo } from 'react';
import { formatCurrency } from '../../../utils/formatCurrency';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  FaPlus, FaEdit, FaTrash, FaFilter, FaDownload, FaFileInvoice, 
  FaMoneyBillWave, FaChartLine, FaCalendarAlt, FaTag, FaUserTie,
  FaCheckCircle, FaTimesCircle, FaClock, FaWallet, FaReceipt
} from 'react-icons/fa';
import {
  getAccountingRecords,
  createAccountingRecord,
  updateAccountingRecord,
  deleteAccountingRecord,
  getAccountingCategories,
  getVendors,
  Accounting,
  AccountingCategory,
  Vendor,
  cacheKeys
} from '../../../firebase/accountingFirestoreService';
import AddEditAccountingModal from '../accounting/AddEditAccountingModal';
import AccountingFilters from '../accounting/AccountingFilters';
import { useUI } from '../../../components/common/UIMessageProvider';
import { cache } from '../../../utils/cacheManager';

interface AccountingFiltersState {
  status: string[];
  category: string[];
  paymentMethod: string[];
  dateFrom: string;
  dateTo: string;
  minAmount: number | '';
  maxAmount: number | '';
}



const AccountingManagement: React.FC = () => {
  const auth = useAuth();
  const canViewAccounting = auth.canPerformAction('accounting', 'view');
  const canAddAccounting = auth.canPerformAction('accounting', 'create');
  const canEditAccounting = auth.canPerformAction('accounting', 'edit');
  const canDeleteAccounting = auth.canPerformAction('accounting', 'delete');
  const canExportAccounting = auth.canPerformAction('accounting', 'export');

  const [records, setRecords] = useState<Accounting[]>([]);
  const [categories, setCategories] = useState<AccountingCategory[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Accounting | null>(null);
  const [filters, setFilters] = useState<AccountingFiltersState>({
    status: [],
    category: [],
    paymentMethod: [],
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: ''
  });
  const ui = useUI();

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [recordsResult, categoriesResult, vendorsResult] = await Promise.all([
        cache.get(cacheKeys.recordsAll, () => getAccountingRecords(), 30 * 60 * 1000),
        cache.get(cacheKeys.categories, () => getAccountingCategories(), 24 * 60 * 60 * 1000),
        cache.get(cacheKeys.vendors, () => getVendors(), 24 * 60 * 60 * 1000)
      ]);

      if (recordsResult.success) setRecords(recordsResult.data || []);
      if (categoriesResult.success) setCategories(categoriesResult.data || []);
      if (vendorsResult.success) setVendors(vendorsResult.data || []);
    } catch (error) {
      console.error('Error fetching accounting data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    setShowModal(true);
  };

  const handleEdit = (record: Accounting) => {
    setEditingRecord(record);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    const ok = await ui.askConfirm({
      title: 'Delete record',
      message: 'Are you sure you want to delete this record?',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!ok) return;
    try {
      const result = await deleteAccountingRecord(id);
      if (result.success) {
        setRecords(prev => prev.filter(r => r.id !== id));
        ui.showMessage('Record deleted', { type: 'success' });
      } else {
        ui.showMessage('Failed to delete record', { type: 'error' });
      }
    } catch (err) {
      console.error('Error deleting record:', err);
      ui.showMessage('Failed to delete record', { type: 'error' });
    }
  };

  const handleSaveRecord = async (record: Partial<Accounting>) => {
    if (editingRecord?.id) {
      // Update existing
      const result = await updateAccountingRecord(editingRecord.id, record);
      if (result.success) {
        setRecords(prev => prev.map(r => r.id === editingRecord.id ? { ...r, ...record } : r));
      }
    } else {
      // Create new
      const result = await createAccountingRecord(record as Accounting);
      if (result.success && result.id) {
        setRecords(prev => [...prev, { ...record, id: result.id } as Accounting]);
      }
    }
    setShowModal(false);
  };

  // Apply filters
  const filteredRecords = useMemo(() => {
    return records.filter(record => {
      // Normalize status: treat 'Paid' as 'Approved'
      const statusNormalized = record.status === 'Paid' ? 'Approved' : record.status;
      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(statusNormalized)) {
        return false;
      }
      
      // Category filter
      if (filters.category.length > 0 && !filters.category.includes(record.category)) {
        return false;
      }
      
      // Payment method filter
      if (filters.paymentMethod.length > 0 && !filters.paymentMethod.includes(record.paymentMethod)) {
        return false;
      }
      
      // Date range filter
      if (filters.dateFrom && record.date < filters.dateFrom) {
        return false;
      }
      if (filters.dateTo && record.date > filters.dateTo) {
        return false;
      }
      
      // Amount range filter
      if (filters.minAmount !== '' && record.amount < Number(filters.minAmount)) {
        return false;
      }
      if (filters.maxAmount !== '' && record.amount > Number(filters.maxAmount)) {
        return false;
      }
      
      return true;
    });
  }, [records, filters]);

  // Calculate totals
  const totals = useMemo(() => {
    // Treat 'Paid' as 'Approved' for totals
    const approved = filteredRecords.filter(r => r.status === 'Approved' || r.status === 'Paid');
    const pending = filteredRecords.filter(r => r.status === 'Pending');
    const paid = filteredRecords.filter(r => r.status === 'Paid');
    const rejected = filteredRecords.filter(r => r.status === 'Rejected');
    
    return {
      total: filteredRecords.reduce((sum, r) => sum + r.amount, 0),
      approved: approved.reduce((sum, r) => sum + r.amount, 0),
      pending: pending.reduce((sum, r) => sum + r.amount, 0),
      paid: paid.reduce((sum, r) => sum + r.amount, 0),
      rejected: rejected.reduce((sum, r) => sum + r.amount, 0)
    };
  }, [filteredRecords]);

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      Pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      Approved: 'bg-blue-100 text-blue-800 border-blue-200',
      Paid: 'bg-green-100 text-green-800 border-green-200',
      Rejected: 'bg-red-100 text-red-800 border-red-200'
    };

    const icons: Record<string, JSX.Element> = {
      Pending: <FaClock className="w-3 h-3" />,
      Approved: <FaCheckCircle className="w-3 h-3" />,
      Paid: <FaWallet className="w-3 h-3" />,
      Rejected: <FaTimesCircle className="w-3 h-3" />
    };

    const color = colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
    const icon = icons[status] || <FaClock className="w-3 h-3" />;

    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${color}`}>
        {icon}
        {status}
      </div>
    );
  };

  const getCategoryColor = (category: string) => {
    const categoryObj = categories.find(c => c.name === category);
    return categoryObj?.color || '#3b82f6';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Accounting</h1>
            <p className="text-sm text-gray-600">Transactions & records</p>
          </div>
          {canAddAccounting && (
            <button onClick={handleAddNew} className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-md text-sm font-medium">
              <FaPlus className="h-4 w-4" />
              Add
            </button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600">Total</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(totals.total)}</p>
              </div>
              <FaMoneyBillWave className="h-6 w-6 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600">Approved</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(totals.approved)}</p>
              </div>
              <FaCheckCircle className="h-6 w-6 text-green-500" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-yellow-600">Pending</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(totals.pending)}</p>
              </div>
              <FaClock className="h-6 w-6 text-yellow-500" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600">Paid</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(totals.paid)}</p>
              </div>
              <FaWallet className="h-6 w-6 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600">Rejected</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(totals.rejected)}</p>
              </div>
              <FaTimesCircle className="h-6 w-6 text-red-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <AccountingFilters 
          filters={filters}
          setFilters={setFilters}
          categories={categories}
        />
      </div>

      {/* Data Grid */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID & Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No accounting records found
                  </td>
                </tr>
              ) : (
                filteredRecords.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          #{record.accountingId}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {record.description}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: getCategoryColor(record.category) }}
                        />
                        <span className="text-sm text-gray-900">{record.category}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(record.amount)} {record.currency}
                      </div>
                      <div className="text-xs text-gray-500">
                        {record.paymentMethod}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(record.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(record.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {record.vendor || 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {canEditAccounting && (
                          <button
                            onClick={() => handleEdit(record)}
                            className="text-blue-600 hover:text-blue-900 p-1.5 rounded hover:bg-blue-50"
                            title="Edit"
                          >
                            <FaEdit className="h-4 w-4" />
                          </button>
                        )}
                        {canDeleteAccounting && (
                          <button
                            onClick={() => record.id && handleDelete(record.id)}
                            className="text-red-600 hover:text-red-900 p-1.5 rounded hover:bg-red-50"
                            title="Delete"
                          >
                            <FaTrash className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <AddEditAccountingModal
          record={editingRecord}
          onClose={() => setShowModal(false)}
          onSave={handleSaveRecord}
          categories={categories}
          vendors={vendors}
        />
      )}
    </div>
  );
};

export default AccountingManagement;
